import { normalizePosIntegration, isPosBranchLinked } from "utils/posBranchSyncUtils"

/**
 * Resolve brand integration code from brand / order-details payload.
 * Prefer brand.integration — never rely on order.serviceProvider for UI gating.
 */
export const resolveBrandIntegration = (source = {}) => {
  if (!source || typeof source !== "object") {
    return ""
  }
  return String(
    source.brandIntegration ||
      source.integration ||
      source.integrationCode ||
      source.brand?.integration ||
      source.brand?.integrationCode ||
      source.posUi?.integration ||
      ""
  )
    .trim()
    .toLowerCase()
}

export const PUSH_CAPABLE_POS_CODES = ["syrve", "foodics", "wulooj-pos"]

export const resolveOrderPosIntegrationCode = (source = {}) =>
  normalizePosIntegration(resolveBrandIntegration(source))

/** F&B order push to external POS (not Salasa e-store). */
export const isPushCapablePosBrand = (source = {}) =>
  PUSH_CAPABLE_POS_CODES.includes(resolveOrderPosIntegrationCode(source))

/** Brand uses Syrve, Foodics, Wulooj POS, or Salasa (legacy listings / export). */
export const hasExternalPosIntegration = (source = {}) => {
  const integration = resolveBrandIntegration(source)
  return (
    integration === "syrve" ||
    integration === "salasa" ||
    integration === "foodics" ||
    integration === "wulooj-pos"
  )
}

export const isSalasaProvider = (source = {}) =>
  resolveBrandIntegration(source) === "salasa"

export const isSyrveProvider = (source = {}) =>
  resolveBrandIntegration(source) === "syrve"

export const isFoodicsProvider = (source = {}) =>
  resolveBrandIntegration(source) === "foodics"

export const isWuloojPosProvider = (source = {}) =>
  resolveOrderPosIntegrationCode(source) === "wulooj-pos"

/** F&B POS (Syrve, Foodics, Wulooj) — shared order list / details UI. */
export const isFnbPosProvider = (source = {}) => isPushCapablePosBrand(source)

export const findAssignedBrandIntegration = (brandId, brands = []) => {
  const id = String(brandId || "").trim()
  if (!id) {
    return ""
  }
  const row = (brands || []).find(
    entry => String(entry?._id || entry?.brandId || "") === id
  )
  return String(row?.integration || "").trim()
}

export const buildOrderPosSource = ({
  order = {},
  brandConfig = null,
  assignedBrands = [],
} = {}) => {
  const brandIntegration =
    order?.brandIntegration ||
    brandConfig?.integration ||
    findAssignedBrandIntegration(order?.brandId, assignedBrands) ||
    order?.serviceProvider ||
    ""
  return {
    ...order,
    brandIntegration,
    integration: brandConfig?.integration || order?.brand?.integration,
    posUi: order?.posUi,
  }
}

export const getTenantIntegrationConnection = (
  integrationCode,
  tenantIntegrationsByCode = {},
  orderPosUi = null
) => {
  const code = normalizePosIntegration(integrationCode)
  if (!code) {
    return {
      code: "",
      status: "not_connected",
      connected: false,
    }
  }

  if (
    orderPosUi &&
    normalizePosIntegration(orderPosUi.integration) === code &&
    orderPosUi.status
  ) {
    const status = String(orderPosUi.status || "").toLowerCase()
    return {
      code,
      status,
      connected: Boolean(orderPosUi.connected),
    }
  }

  const row = tenantIntegrationsByCode[code]
  const status = String(row?.status || "not_connected").toLowerCase()
  return {
    code,
    status,
    connected: status === "connected" && row?.isEnabled !== false,
  }
}

export const isOrderPosBranchLinked = (order, posIntegrationCode) =>
  isPosBranchLinked(order, normalizePosIntegration(posIntegrationCode))

/** Unified push/sync state for display and manual retry. */
export const resolveOrderPosPushState = (order = {}) => {
  const ps = order?.posSettings || {}
  const hasRef = Boolean(order?.posReferenceId)
  const pushStatus = ps.status ? String(ps.status) : ""
  const lastOutcome = ps.lastPushOutcome
    ? String(ps.lastPushOutcome).toLowerCase()
    : ""

  if (hasRef || pushStatus === "Success") {
    return {
      key: "success",
      displayStatus: pushStatus || "Success",
      canRetry: false,
    }
  }
  if (pushStatus === "Error" || lastOutcome === "failure") {
    return {
      key: "failed",
      displayStatus: pushStatus || "Error",
      canRetry: true,
    }
  }
  if (lastOutcome === "skipped") {
    return {
      key: "skipped",
      displayStatus: "Skipped",
      reason: ps.lastPushReason || "",
      canRetry: true,
    }
  }
  if (!hasRef && !lastOutcome && !pushStatus) {
    return {
      key: "not_pushed",
      displayStatus: "-",
      canRetry: true,
    }
  }
  return {
    key: "unknown",
    displayStatus: pushStatus || "-",
    canRetry: !hasRef,
  }
}

export const shouldShowOrderPosDetails = (source = {}) =>
  isPushCapablePosBrand(source)

export const canShowManualPosPushButton = ({
  order = {},
  posIntegrationCode = "",
  tenantConnected = false,
  branchLinked = false,
  canEdit = false,
} = {}) => {
  if (!canEdit) {
    return false
  }
  if (!isPushCapablePosBrand({ brandIntegration: posIntegrationCode })) {
    return false
  }
  if (!tenantConnected) {
    return false
  }
  if (!branchLinked) {
    return false
  }
  const pushState = resolveOrderPosPushState(order)
  if (pushState.key === "success") {
    return false
  }
  return Boolean(pushState.canRetry)
}

export const getPosIntegrationDisplayName = (code, t) => {
  const normalized = normalizePosIntegration(code)
  if (normalized === "wulooj-pos") {
    return t("Wulooj POS")
  }
  if (normalized === "foodics") {
    return t("Foodics")
  }
  if (normalized === "syrve") {
    return t("Syrve")
  }
  return normalized || "-"
}

/**
 * POS stores a JSON envelope in errorInfo / lastPushReason. Show only
 * `error.message` (e.g. "Payments (10) must equal the total (160)").
 */
export const formatPosPushErrorMessage = (value) => {
  if (value == null || value === "") {
    return ""
  }
  if (typeof value === "object") {
    const nested =
      value?.error?.message ||
      value?.message ||
      value?.error?.error?.message ||
      ""
    if (typeof nested === "string" && nested.trim()) {
      return nested.trim()
    }
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  const text = String(value).trim()
  if (!text) {
    return ""
  }
  if (text.startsWith("{") || text.startsWith("[")) {
    try {
      return formatPosPushErrorMessage(JSON.parse(text))
    } catch {
      return text
    }
  }
  return text
}

export const getTenantPosConnectionLabel = (connection, t) => {
  if (!connection?.code) {
    return t("Not connected")
  }
  if (connection.connected) {
    return t("Connected")
  }
  const status = String(connection.status || "").toLowerCase()
  if (status === "disconnected") {
    return t("Disconnected")
  }
  return t("Not connected")
}
